<template>
    <div class="ui-page">
        <ui-page-header :title="client ? client.name : 'Carregando...'" back="/fiscalizacao-mensal" back-text="Painel RMA">
            <template #title-append>
                <ui-badge v-if="settings" :status="settings.active ? 'active' : 'inactive'" :text="settings.active ? 'RMA ativo' : 'RMA desativado'" />
            </template>
            <template #subtitle>
                <span v-if="settings && settings.active">
                    Prazo: dia {{ settings.due_day }} do mês seguinte · lembrete {{ settings.reminder_days_before }} dias antes ·
                    {{ settings.overdue_interval_days ? `nova cobrança a cada ${settings.overdue_interval_days} dias após o prazo` : "sem cobrança recorrente" }}
                </span>
                <span v-else-if="settings">Configure e ative a fiscalização mensal na aba Configuração.</span>
            </template>
            <v-btn outlined :disabled="!competences.length" @click="archiveOpen = true"><v-icon left small>mdi-folder-zip-outline</v-icon>Baixar documentos (.zip)</v-btn>
            <v-btn v-if="settings && settings.active" color="primary" depressed @click="tab = 2; $nextTick(() => $refs.settings && $refs.settings.focusOpen())">
                <v-icon left small>mdi-calendar-plus</v-icon>Abrir competência
            </v-btn>
        </ui-page-header>

        <v-tabs v-model="tab" class="rma-case-tabs">
            <v-tab>Documentos<span v-if="current && current.counts.missing" class="ui-tab-count ui-tab-count--attention">{{ current.counts.missing }}</span></v-tab>
            <v-tab>Histórico</v-tab>
            <v-tab>Configuração</v-tab>
            <v-tab>Questionário</v-tab>
        </v-tabs>

        <div v-if="tab <= 1 && competences.length" class="d-flex align-center flex-wrap mt-5" style="gap: 12px;">
            <v-select v-model="competenceId" :items="competenceItems" item-value="id" item-text="text" label="Competência" outlined dense hide-details
                style="max-width: 280px;" :menu-props="{ offsetY: true }"></v-select>
            <template v-if="current">
                <ui-badge set="rma-competence" :status="current.status" />
                <span class="ui-small ui-muted">
                    Prazo {{ current.due_date_label }}<span v-if="current.overdue" style="color: #b91c1c;"> · encerrado</span>
                    · {{ current.counts.delivered }} de {{ current.counts.total }} documentos enviados
                    <span v-if="!current.requested_at"> · solicitação ainda não enviada</span>
                    <span v-if="current.collection_stopped_at"> · cobrança encerrada</span>
                </span>
            </template>
        </div>

        <v-tabs-items v-model="tab" class="pt-5">
            <v-tab-item>
                <rma-checklist v-if="competenceId" :key="`checklist-${competenceId}`" :competence-id="competenceId" @changed="onCompetenceChanged" />
                <section v-else class="ui-card">
                    <ui-empty icon="mdi-calendar-blank-outline">
                        <template v-if="settings && settings.active">
                            Nenhuma competência aberta. A primeira abre sozinha no dia 1 do mês seguinte à competência inicial ({{ startLabel }}),
                            ou abra agora em "Abrir competência".
                        </template>
                        <template v-else>A fiscalização mensal deste caso ainda não está ativa. Configure na aba Configuração.</template>
                    </ui-empty>
                </section>
            </v-tab-item>
            <v-tab-item>
                <rma-timeline v-if="current" :key="`timeline-${competenceId}-${timelineVersion}`" :competence="current" @changed="reloadCompetences" />
                <section v-else class="ui-card"><ui-empty icon="mdi-timeline-outline" text="Sem competência aberta." /></section>
            </v-tab-item>
            <v-tab-item>
                <rma-settings-form ref="settings" :client-id="clientId" :competences="competences" @saved="onSettingsSaved" @opened="onCompetenceOpened" />
            </v-tab-item>
            <v-tab-item>
                <rma-questionnaire-tab v-if="tab === 3" :client-id="clientId" :open-id="questionnaireId" />
            </v-tab-item>
        </v-tabs-items>

        <rma-archive-dialog v-model="archiveOpen" :client-id="clientId" :competences="competences" :companies="companies" :document-types="documentTypes"
            :current-key="current ? current.key : null" />
    </div>
</template>

<script>
import axios from "axios";
import RmaChecklist from "@/components/rma/RmaChecklist.vue";
import RmaTimeline from "@/components/rma/RmaTimeline.vue";
import RmaSettingsForm from "@/components/rma/RmaSettingsForm.vue";
import RmaQuestionnaireTab from "@/components/rma/RmaQuestionnaireTab.vue";
import RmaArchiveDialog from "@/components/rma/RmaArchiveDialog.vue";
import { competenceLabel, notifyError } from "@/components/rma/rma.util";

const TABS = { documentos: 0, historico: 1, configuracao: 2, questionario: 3 };

export default {
    name: "RmaCaseDetails",

    components: { RmaChecklist, RmaTimeline, RmaSettingsForm, RmaArchiveDialog, RmaQuestionnaireTab },

    data: () => ({
        tab: 0,
        client: null,
        settings: null,
        companies: [],
        documentTypes: [],
        competences: [],
        competenceId: null,
        archiveOpen: false,
        timelineVersion: 0,
    }),

    computed: {
        questionnaireId() {
            return Number(this.$route.query.questionario) || null;
        },
        clientId() {
            return Number(this.$route.params.clientId);
        },
        current() {
            return this.competences.find((item) => item.id === this.competenceId) || null;
        },
        competenceItems() {
            return this.competences.map((item) => ({ id: item.id, text: `${item.label} · ${item.status_label}` }));
        },
        startLabel() {
            return this.settings && this.settings.start_competence ? competenceLabel(this.settings.start_competence) : "—";
        },
    },

    watch: {
        competenceId(value) {
            if (value && String(this.$route.query.competencia) !== String(value)) {
                this.$router.replace({ query: { ...this.$route.query, competencia: value } }).catch(() => {});
            }
        },
    },

    async created() {
        if (this.$route.query.aba && TABS[this.$route.query.aba] !== undefined) this.tab = TABS[this.$route.query.aba];
        // Competencia pedida na URL (link do Painel RMA); lida antes de carregar a lista.
        const wanted = Number(this.$route.query.competencia) || null;
        await Promise.all([this.loadSettings(), this.reloadCompetences(wanted)]);
        if (this.settings && !this.settings.active && !this.competences.length) this.tab = 2;
    },

    methods: {
        async loadSettings() {
            try {
                const result = await axios.get(`/api/admin/rma/clients/${this.clientId}/settings`);
                this.applySettings(result.data.content);
            } catch (error) {
                notifyError(this, error);
            }
        },

        applySettings(content) {
            this.client = content.client;
            this.settings = content.settings;
            this.companies = content.companies;
            this.documentTypes = content.document_types;
        },

        async reloadCompetences(preferredId = null) {
            try {
                const result = await axios.get(`/api/admin/rma/clients/${this.clientId}/competences`);
                this.competences = result.data.content;
                if (preferredId && this.competences.some((item) => item.id === preferredId)) {
                    this.competenceId = preferredId;
                } else if (!this.competences.some((item) => item.id === this.competenceId)) {
                    this.competenceId = this.competences.length ? this.competences[0].id : null;
                }
                this.timelineVersion += 1;
            } catch (error) {
                notifyError(this, error);
            }
        },

        // O checklist devolve a competencia atualizada a cada acao (contagens e status).
        onCompetenceChanged(competence) {
            const index = this.competences.findIndex((item) => item.id === competence.id);
            if (index >= 0) this.$set(this.competences, index, { ...this.competences[index], ...competence });
            this.timelineVersion += 1;
        },

        onSettingsSaved(content) {
            this.applySettings(content);
            this.reloadCompetences();
        },

        async onCompetenceOpened(competence) {
            await this.reloadCompetences();
            this.competenceId = competence.id;
            this.tab = 0;
        },
    },
};
</script>

<style scoped>
/* Tres abas nunca transbordam: evita o deslocamento do slide-group do Vuetify
   quando a largura arredonda para mais (a primeira aba aparecia cortada). */
.rma-case-tabs ::v-deep .v-slide-group__content {
    transform: none !important;
}
</style>
